import {
  BrowserRouter as Router, Routes, Route,
} from 'react-router-dom';
import { useContext } from 'react';
import PropTypes from 'prop-types';
import { AuthProvider, AuthContext } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { SocketProvider } from './context/SocketContext';
import Header from './components/Header';
import UnauthenticatedHeader from './components/UnauthenticatedHeader';
import Footer from './components/Footer';
import UnauthenticatedFooter from './components/UnauthenticatedFooter';
import ScrollToTop from './components/ScrollToTop';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import Landing from './pages/Landing';
import Play from './pages/Play';
import Leaderboard from './pages/Leaderboard';
import Profile from './pages/Profile';
import Login from './pages/Login';
import Register from './pages/Register';
import ContributorRoute from './components/ContributorRoute';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import SpriteReview from './pages/admin/SpriteReview';
import AdminNpcs from './pages/admin/AdminNpcs';
import AdminBusinesses from './pages/admin/AdminBusinesses';
import AdminWorlds from './pages/admin/AdminWorlds';
import WorldEditor from './pages/admin/WorldEditor';
import SpriteEditor from './pages/SpriteEditor';

function App() {
  return (
    <ThemeProvider>
      <Router>
        <ScrollToTop />
        <AuthProvider>
          <div className="flex min-h-screen w-full overflow-x-hidden flex-col">
            <div className="flex flex-1">
              <ConditionalHeader />

              <MainContent>
                <Routes>
                  <Route path="/" element={<LandingRoute />} />
                  <Route path="/leaderboard" element={<Leaderboard />} />
                  <Route
                    path="/profile"
                    element={(
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    )}
                  />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  {/*
                    SocketProvider is scoped to /play rather than wrapping the
                    whole app, so browsing the landing page or leaderboard does
                    not hold an open socket. It mounts inside ProtectedRoute so
                    a connection is only attempted once a token exists.
                  */}
                  <Route
                    path="/play"
                    element={(
                      <ProtectedRoute>
                        <SocketProvider>
                          <Play />
                        </SocketProvider>
                      </ProtectedRoute>
                    )}
                  />
                  <Route
                    path="/admin"
                    element={(
                      <AdminRoute>
                        <AdminDashboard />
                      </AdminRoute>
                    )}
                  />
                  <Route
                    path="/admin/users"
                    element={(
                      <AdminRoute>
                        <AdminUsers />
                      </AdminRoute>
                    )}
                  />
                  {/*
                    The editor and the review queue are different jobs with
                    different audiences: contributors draw, admins decide.
                    Both client gates are advisory — spriteAccess.js on the
                    server is what actually enforces either one.
                  */}
                  <Route
                    path="/sprites"
                    element={(
                      <ContributorRoute>
                        <SpriteEditor />
                      </ContributorRoute>
                    )}
                  />
                  <Route
                    path="/admin/sprites"
                    element={(
                      <AdminRoute>
                        <SpriteReview />
                      </AdminRoute>
                    )}
                  />
                  {/*
                    Editing the cast. Characters are CREATED in-game by walking
                    a route — see components/game/NpcRecorder — because a route
                    you walked is walkable by construction. This page is for
                    everything easier done sitting still.
                  */}
                  <Route
                    path="/admin/npcs"
                    element={(
                      <AdminRoute>
                        <AdminNpcs />
                      </AdminRoute>
                    )}
                  />
                  {/*
                    When each place is open. NOT part of the world editor, whose
                    Save writes a draft that needs publishing — hours live in
                    their own collection so they survive a republish, and they
                    apply the moment they are saved.
                  */}
                  <Route
                    path="/admin/businesses"
                    element={(
                      <AdminRoute>
                        <AdminBusinesses />
                      </AdminRoute>
                    )}
                  />
                  {/*
                    Where players can go. Separate from /admin/npcs because the
                    two answer different questions — this one is the place, that
                    one is who is in it — and because a world being off has to
                    be findable without hunting through a cast list.
                  */}
                  <Route
                    path="/admin/worlds"
                    element={(
                      <AdminRoute>
                        <AdminWorlds />
                      </AdminRoute>
                    )}
                  />
                  <Route
                    path="/admin/worlds/:name"
                    element={(
                      <AdminRoute>
                        <WorldEditor />
                      </AdminRoute>
                    )}
                  />
                </Routes>
                <ConditionalFooter />
              </MainContent>
            </div>
          </div>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

/**
 * Main content wrapper
 */
function MainContent({ children }) {
  return (
    <main className="flex-1 transition-all duration-300 overflow-x-hidden max-w-full pt-20">
      {children}
    </main>
  );
}

MainContent.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Conditionally renders the appropriate header based on authentication state.
 * Waits for auth loading to complete to prevent flicker.
 */
function ConditionalHeader() {
  const { token, loading } = useContext(AuthContext);

  if (loading) {
    return null;
  }

  return token ? <Header /> : <UnauthenticatedHeader />;
}

/**
 * Conditionally renders the appropriate footer based on authentication state.
 */
function ConditionalFooter() {
  const { token, loading } = useContext(AuthContext);

  if (loading) {
    return null;
  }

  return token ? <Footer /> : <UnauthenticatedFooter />;
}

/**
 * Renders the landing page.
 * Waits for auth loading to complete before rendering to prevent flicker.
 */
function LandingRoute() {
  const { loading } = useContext(AuthContext);

  if (loading) {
    return null;
  }

  return <Landing />;
}

export default App;
